import {
  Autocomplete,
  Button,
  Checkbox,
  ComboboxItem,
  Divider,
  Group,
  Modal,
  NumberInput,
  OptionsFilter,
  ScrollArea,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useListState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  deleteAllCrInvoiceDetails,
  deleteCrInvoiceDetails,
  getCrInvoiceAndDetails,
  getOrderInfoByInvoice,
  submitCrInvoice,
} from "api/creditnote.service";
import { logError } from "api/diagnostic.service";
import React, {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IDataEntryFormProps } from "types/interface.types";
import {
  CreditNoteDataEntryForm,
  CreditNoteEntries,
  CreditNoteEntry,
  CreditNoteProductEntries,
  JournalEntries,
} from "types/model.types";

import { IconCheck, IconDeviceFloppy, IconPrinter } from "@tabler/icons-react";

import { getKnownInvoicesByDebtor } from "api/invoice.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_ERROR_TITLE,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_DATA_LOAD_TITLE,
  MSG_DEFAULT_FORM_INVALID,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
  NOTIFICATION_TITLE_CRINVOICE,
} from "common/constants";
import {
  calculateGSTFromNet,
  formatDate,
  getLocalDateString,
} from "common/utils";
import { useProgressiveDebtorFetch } from "hooks/useProgressiveDebtorFetch";
import { FullScreenModal, SavingModal } from "pages/modals";
import { CreditNoteHeaderRow } from "./DataEntryHeaderDefinitions";

import { safeApiCall } from "api/common.service";
import { navigateTo } from "common/navigation";
import DefaultButtonAdd from "pages/admin/components/DefaultButtonAdd";
import { debounce } from "utils/helper";
import { CreditNoteEntryRow } from "./AdminCreditNoteEntryRow";
import AdminCreditInvoicePreview from "./AdminCreditNotePreview";

const AdminCreditNoteEntryPage: React.FC<IDataEntryFormProps> = ({
  recordId,
  onSaveComplete,
}) => {
  const apiFetchRef1 = useRef(false);
  const orderLineSelectionResolvedRef = useRef(false);

  const creditToRef = useRef<HTMLInputElement>(null);
  const invoiceRef = useRef<HTMLInputElement>(null);
  const [headerVersion, setHeaderVersion] = useState(0);

  const [debtorInputValue, setDebtorInputValue] = useState("");
  const [creditTo, setCreditTo] = useState<number | null>(null);

  const [invoiceInputValue, setInvoiceInputValue] = useState("");
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [orderDetails, setOrderDetails] = useState<CreditNoteProductEntries>(
    [],
  );

  // Ids of the *order lines* (OrderDetails.id) currently checked in the
  // "Select Product" modal -- not productIds. An invoice can have multiple
  // order lines for the same product (e.g. several cheese lines with
  // different quantities), and only the order line's own id can tell those
  // rows apart; productId cannot.
  const [selectedOrderLineIds, setSelectedOrderLineIds] = useState<string[]>(
    [],
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [debtorInvoices, setDebtorInvoices] = useState<JournalEntries>([]); // if creditTo changes, we fetch all invoices for that debtor
  const {
    debtors,
    loading: debtorLoading,
    error: debtorError,
  } = useProgressiveDebtorFetch();
  const [fetchingInvoices, setFetchingInvoices] = useState(false);
  const [preview, setPreview] = useState(false);
  const isEditing = (recordId ?? 0) > 0;
  const [saving, setSaving] = React.useState(false);
  const [initialData, setInitialData] =
    useState<CreditNoteDataEntryForm | null>(null);
  const debtorOptions: ComboboxItem[] = debtors.map((debtor) => ({
    value: `${debtor.id}`,
    label: `${debtor.name} [${debtor.id}]`,
  }));
  const optionsFilter: OptionsFilter = ({ options, search }) => {
    const searchWords = search.toLowerCase().trim().split(" ");

    return (options as ComboboxItem[]).filter((option) => {
      const labelWords = option.label?.toLowerCase().trim().split(" ") ?? [];
      return searchWords.every((word) =>
        labelWords.some((labelWord) => labelWord.includes(word)),
      );
    });
  };
  const [entries, handlers] = useListState<CreditNoteEntry>([]);
  const invoiceOptionsFilter: OptionsFilter = ({ options, search }) => {
    const searchWords = search.toLowerCase().trim().split(" ");

    return (options as ComboboxItem[]).filter((option) => {
      const labelWords = option.label?.toLowerCase().trim().split(" ") ?? [];
      return searchWords.every((word) =>
        labelWords.some((labelWord) => labelWord.includes(word)),
      );
    });
  };
  const invoiceOptions: ComboboxItem[] = debtorInvoices.map((inv) => ({
    value: `${inv.invoiceId}`,
    label: `Invoice #: ${inv.invoiceId} on Date: ${formatDate(
      inv.createdOn,
    )}, Invoice Amount: ${inv.amount}`,
  }));
  // Define our useForm and must be declared before any UseEffects
  const form = useForm<CreditNoteDataEntryForm>({
    initialValues: initialData ?? {
      creditNoteSummary: {
        id: 0,
        createdOn: getLocalDateString(),
        gst: 0,
        creditTo: 0,
        amount: 0,
        invoiceId: 0,
        total: 0,
        notes: "",
        isPrinted: false,
        isProcessed: false,
      },
      creditNoteEntries: [],
      productEntries: [],
    },
  });
  const { creditNoteSummary: summary } = form.getValues();
  // this removes state updates from layout-sensitve effects and removes layout feedback loop.
  // otherwise, we'll randomly get ResizeObserver loop error
  const { rounded, gst } = useMemo(() => {
    const total = entries.reduce((sum, item) => {
      const isEach = (item.productCode ?? "C").toUpperCase() === "E";
      const unitPrice = isEach ? item.eachPrice : item.cartonPrice;
      const discount = item.discount > 0 ? item.discount : 0;

      return sum + item.returned * unitPrice * (1 - discount);
    }, 0);
    const rounded = Math.round(total * 100) / 100;

    return { rounded, gst: calculateGSTFromNet(rounded) };
  }, [entries]);

  useEffect(() => {
    form.setFieldValue("creditNoteSummary.amount", rounded);
    form.setFieldValue("creditNoteSummary.gst", gst);
  }, [rounded, gst]);

  // When creditTo changes, fetch invoices for that debtor
  useEffect(() => {
    if (!creditTo || creditTo === 0) return;

    let mounted = true;
    setFetchingInvoices(true);
    setInvoiceInputValue("");

    const currentInvoiceId = form.getValues().creditNoteSummary.invoiceId;
    if (currentInvoiceId !== 0) {
      form.setFieldValue("creditNoteSummary.invoiceId", 0);
    }

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getKnownInvoicesByDebtor(creditTo, 12),
        );
        if (mounted) setDebtorInvoices(response ?? []);
      } catch (error) {
        logError(error);
        notifications.show({
          title: MSG_DEFAULT_ERROR_TITLE,
          message: "Failed to fetch invoices for selected debtor.",
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        if (mounted) setFetchingInvoices(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [creditTo]);

  // listen to keydown / esc button
  // and if hit, save first so user doesn't loose data
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleConfirmSelection(); // confirm selection 1st
        setModalOpen(false); // then close
      }
    };

    if (modalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen, selectedOrderLineIds]);

  // when invoiceInputValue changes, if it matches an invoice, set invoiceId in form state
  // and load the invoice details
  useEffect(() => {
    if (!invoiceId || (Number(invoiceId) ?? 0) === 0) return;
    let mounted = true;

    const fetchData = async () => {
      try {
        const response = await safeApiCall(getOrderInfoByInvoice(invoiceId));
        if (response && mounted) setOrderDetails(response ?? []);
      } catch (error: any) {
        logError(error);
        notifications.show({
          title: MSG_DEFAULT_ERROR_TITLE,
          message: `Failed to fetch order details for selected invoice.`,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [invoiceId]);

  // monitor record id... set if user editing
  useEffect(() => {
    if (!recordId || (recordId ?? 0) === 0) return;
    let mounted = true;

    const fetchData = async () => {
      try {
        const response = await safeApiCall(getCrInvoiceAndDetails(recordId));
        if (!mounted) return;

        setInvoiceId(response.creditNoteSummary.invoiceId);
        setCreditTo(response.creditNoteSummary.creditTo);
        // selectedOrderLineIds is resolved once orderDetails has loaded too
        // (see the effect below) -- it needs to map each saved entry back to
        // a specific order line, not just a productId.
        setInitialData(response);
      } catch (error) {
        logError(error);
        notifications.show({
          title: MSG_DEFAULT_FORM_DATA_LOAD_TITLE,
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          color: MantineNotificationColor.Error,
        });
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [recordId]);

  // Resolve which order lines back this credit note's saved entries, once
  // both the saved entries (initialData) and the original invoice's order
  // lines (orderDetails) have loaded. Entries saved with an orderDetailId
  // map directly; older entries saved before that link existed fall back to
  // claiming the first not-yet-claimed order line with a matching productId.
  useEffect(() => {
    if (
      !isEditing ||
      !initialData ||
      orderDetails.length === 0 ||
      orderLineSelectionResolvedRef.current
    )
      return;
    orderLineSelectionResolvedRef.current = true;

    const usedOrderLineIds = new Set<number>();
    const resolved: string[] = [];

    for (const entry of initialData.creditNoteEntries) {
      if (entry.orderDetailId) {
        resolved.push(String(entry.orderDetailId));
        usedOrderLineIds.add(entry.orderDetailId);
        continue;
      }
      const match = orderDetails.find(
        (p) =>
          p.productId === entry.productId &&
          !usedOrderLineIds.has(Number(p.id)),
      );
      if (match) {
        resolved.push(String(match.id));
        usedOrderLineIds.add(Number(match.id));
      }
    }

    setSelectedOrderLineIds(resolved);

    return () => {
      orderLineSelectionResolvedRef.current = false;
    };
  }, [isEditing, initialData, orderDetails]);

  useEffect(() => {
    if (!isEditing || !initialData || !debtorInvoices || apiFetchRef1.current)
      return;
    apiFetchRef1.current = true;
    let mounted = true;

    const { creditNoteSummary } = form.getValues();
    const currentCreditTo =
      creditNoteSummary.creditTo > 0
        ? creditNoteSummary.creditTo
        : initialData.creditNoteSummary.creditTo;
    const currentInvoiceId =
      creditNoteSummary.invoiceId > 0
        ? creditNoteSummary.invoiceId
        : initialData.creditNoteSummary.invoiceId;
    const creditLabel = debtorInputValue;

    if (!creditLabel || creditLabel === "") {
      if (!mounted) return;
      if (debtors.length > 0 && !debtorLoading && currentCreditTo > 0) {
        const label = resolveDebtorLabel(currentCreditTo);
        setDebtorInputValue(label);
      }
    }
    const invLabel = invoiceInputValue;
    if (!invLabel || invLabel === "") {
      if (!mounted) return;
      if (debtorInvoices.length > 0 && currentInvoiceId > 0) {
        const invLabel = resolveInvoiceLabel(currentInvoiceId);
        setInvoiceInputValue(invLabel);
      }
    }

    startTransition(() => {
      if (!mounted) return;
      form.setValues({
        creditNoteSummary: {
          ...initialData.creditNoteSummary,
          creditTo: currentCreditTo,
          invoiceId: currentInvoiceId,
        },
        creditNoteEntries: initialData.creditNoteEntries,
        productEntries: initialData.productEntries,
      });

      handlers.setState(initialData.creditNoteEntries);
    });
    return () => {
      mounted = false;
      apiFetchRef1.current = false;
    };
    //}, [initialData, debtors, debtorInvoices, debtorLoading]);
  }, [initialData, debtorInvoices]);

  const resolveDebtorLabel = (id: number): string => {
    return debtorOptions.find((d) => Number(d.value) === id)?.label ?? "";
  };
  const resolveInvoiceLabel = (id: number): string => {
    return invoiceOptions.find((d) => Number(d.value) === id)?.label ?? "";
  };

  const handleRemoveAllEntries = async () => {
    if (summary.isProcessed) return; // should never happen as the remove buttons are disabled when processed, but just in case...

    if (form.values.creditNoteSummary.id === 0) return;
    try {
      await safeApiCall(
        deleteAllCrInvoiceDetails(Number(form.values.creditNoteSummary.id)),
      );
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
        message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (error) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      logError(error);
    }
  };
  const handleRemoveEntries = async (ids: number[]) => {
    if (ids.length === 0) return;
    const { creditNoteSummary } = form.getValues();
    if (creditNoteSummary.isProcessed) return; // should never happen as the remove buttons are disabled when processed, but just in case...
    setSaving(true);

    try {
      const response = await safeApiCall(
        deleteCrInvoiceDetails(Number(creditNoteSummary.id), ids),
      );
      if (response?.status === 200 && response.data?.success) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    } catch (error) {
      logError(error);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_DELETE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEntry = async (index: number) => {
    const entry = entries[index]; // form.values.creditNoteEntries[index];
    const entryId = Number(entry.id); // coerce to number safely

    const removeFromSelected = () => {
      setSelectedOrderLineIds((prev) => {
        if (entry.orderDetailId) {
          return prev.filter((id) => Number(id) !== entry.orderDetailId);
        }
        // Legacy entry with no tracked order line: best-effort removal by
        // productId (may not disambiguate duplicate-product order lines).
        const orderLine = orderDetails.find(
          (p) =>
            p.productId === entry.productId && prev.includes(String(p.id)),
        );
        return orderLine
          ? prev.filter((id) => id !== String(orderLine.id))
          : prev;
      });
    };

    if (!isNaN(entryId) && entryId > 0) {
      handleRemoveEntries([entryId]);
    }
    removeFromSelected();
    handlers.remove(index); // newly added entry, just remove locally
  };

  const handlePrint = async () => {
    form.setFieldValue("creditNoteSummary.isPrinted", true);
    // persist to ensure preview shows correct data
    handleSubmit(false, false, true);
    //setPreview(true);
    const { creditNoteSummary: summary } = form.getValues();
    setTimeout(() => {
      navigateTo(`/print/crnote?id=${summary.id}&e=1`);
    }, 500);
  };

  const canProceed = (): boolean => {
    if (entries.length === 0) return true;

    const invalid = entries.filter((e) => {
      const isEach = (e.productCode ?? "C").toUpperCase() === "E";
      const unitsPerCarton = e.unitsCtn ?? 1; // fallback to 1 to avoid divide-by-zero
      const maxReturnable = isEach ? e.supplied * unitsPerCarton : e.supplied;

      return e.returned > maxReturnable;
    });

    return invalid.length === 0;
  };
  /**
   * Process the credit note.  This works by setting the isProcessed flag to true
   * and then calling handleSubmit to save the form.  The backend will handle the rest.
   * @returns
   */
  const handleProcess = async () => {
    setTimeout(() => {
      handleSubmit(true, true);
    }, 0);
  };

  /**
   * Save the form data to the backend.
   * @param close If true, close the form after saving
   * @returns
   */
  const handleSubmit = async (
    close = false,
    process = false,
    suppressNotification = false,
  ) => {
    const formData = form.getValues();
    if (formData.creditNoteSummary.isProcessed) return; // already processed...

    let originalInvoiceId = formData.creditNoteSummary.invoiceId;
    if (originalInvoiceId !== invoiceId) {
      originalInvoiceId = Number(invoiceId ?? 0);
    }
    if (canProceed() === false || !originalInvoiceId) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_INVALID,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    } // should never happen as the buttons call the canProceed and disables control if form is invalid.

    setSaving(true);
    try {
      // Replace creditNoteEntries with current entries
      const payload: CreditNoteDataEntryForm = {
        ...formData,
        creditNoteSummary: {
          ...formData.creditNoteSummary,
          isProcessed: process,
          invoiceId: originalInvoiceId, // override with latest invoiceId
        },
        creditNoteEntries: entries,
        productEntries: [],
      };

      const result: any = await safeApiCall(submitCrInvoice(payload));
      // Merge backend response into form
      if (
        !result?.creditNoteEntries ||
        !Array.isArray(result.creditNoteEntries)
      ) {
        throw new Error(
          "Invalid response: creditNoteEntries is missing or not an array.",
        );
      }
      const updatedEntries: CreditNoteEntries = result.creditNoteEntries.map(
        (updatedEntry: CreditNoteEntry) => {
          // match on order line first -- the same product can appear on multiple order lines
          const existing = updatedEntry.orderDetailId
            ? entries?.find((e) => e.orderDetailId === updatedEntry.orderDetailId)
            : entries?.find((e) => e.productId === updatedEntry.productId);

          return {
            ...existing,
            ...updatedEntry, // this includes the new `id`
          };
        },
      );
      startTransition(() => {
        form.setValues({
          creditNoteSummary: {
            ...form.values.creditNoteSummary,
            id: result.recordId,
          },
          creditNoteEntries: [],
        });
        handlers.setState(updatedEntries);
      });

      if (!suppressNotification) {
        notifications.show({
          title: NOTIFICATION_TITLE_CRINVOICE,
          position: MantineNotificationPosition.TopCenter,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          color: MantineNotificationColor.Success,
        });
      }

      (process || close) && onSaveComplete(close);
    } catch (error) {
      logError(error);
      notifications.show({
        title: NOTIFICATION_TITLE_CRINVOICE,
        position: MantineNotificationPosition.TopCenter,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        color: MantineNotificationColor.Error,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSelection = () => {
    const selectedIds = new Set(selectedOrderLineIds.map(Number));

    // Entries already known to have come from a specific order line match
    // directly. Entries saved before that link existed (orderDetailId
    // missing) are matched by productId instead, consuming at most one
    // candidate per order line so two selected order lines for the same
    // product don't both grab the same existing entry.
    const existingByOrderLine = new Map<number, CreditNoteEntry>();
    const existingByProductId = new Map<number, CreditNoteEntry[]>();
    for (const e of entries) {
      if (e.orderDetailId) {
        existingByOrderLine.set(e.orderDetailId, e);
      } else {
        const list = existingByProductId.get(e.productId) ?? [];
        list.push(e);
        existingByProductId.set(e.productId, list);
      }
    }

    const combined: CreditNoteEntries = [];

    for (const p of orderDetails) {
      const lineId = Number(p.id);
      if (!selectedIds.has(lineId)) continue;

      const existing =
        existingByOrderLine.get(lineId) ??
        existingByProductId.get(p.productId)?.shift();

      if (existing) {
        combined.push({ ...existing, orderDetailId: lineId }); // preserve edits, backfill the link
      } else {
        combined.push({
          id: 0,
          productId: p.productId,
          orderDetailId: lineId,
          description: p.productDescription,
          unitPrice: p.unitPrice,
          discount: p.discount,
          eachPrice: p.eachPrice,
          unitsCtn: p.unitsCtn,
          supplied: p.supplied,
          cartonPrice: p.cartonPrice,
          productCode: p.productCode ?? "C",
          ordered: p.ordered,
          returned: p.supplied,
        });
      }
    }

    combined.sort((a, b) => a.productId - b.productId);

    const combinedIds = new Set(
      combined.map((c) => Number(c.id)).filter((id) => id > 0),
    );
    const removedIds = entries
      .filter(
        (entry) => Number(entry.id ?? 0) > 0 && !combinedIds.has(Number(entry.id)),
      )
      .map((entry) => Number(entry.id));

    startTransition(() => {
      handlers.setState(combined);
      handleRemoveEntries(removedIds);
      setModalOpen(false);
    });
  };

  const debouncedUpdateRef = useRef(
    debounce(
      (index: number, updated: CreditNoteEntry) => {
        handlers.setItem(index, updated);
      },
      0, // zero delay = async microtask, breaks Virtuoso loop
    ),
  );

  return (
    <Stack>
      <Title order={3}>Credit Note Entry</Title>
      <Group grow align="flex-start">
        <TextInput
          label="Credit ID"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={
            form.values.creditNoteSummary.id === 0
              ? "New"
              : form.values.creditNoteSummary.id
          }
          readOnly
        />
        <TextInput
          label="Created On"
          value={formatDate(form.values.creditNoteSummary.createdOn)}
          readOnly
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
        <NumberInput
          label="Amount"
          title="GST Inclusive"
          readOnly
          {...form.getInputProps("creditNoteSummary.amount")}
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
        />
      </Group>
      <Group grow align="flex-start">
        <Autocomplete
          ref={creditToRef}
          label="Credit To (Debtor)"
          placeholder="Type name or ID"
          data={debtorOptions}
          filter={optionsFilter}
          value={debtorInputValue}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
          onFocus={() => {
            creditToRef.current?.select();
          }}
          onChange={setDebtorInputValue} // updates as user types
          onOptionSubmit={(selected) => {
            const originalLabel = debtorInputValue;
            const match = debtorOptions.find(
              (d) => d.label === selected || d.value === selected,
            );
            const id = match ? Number(match.value) : 0;
            form.setFieldValue("creditNoteSummary.creditTo", id);

            setCreditTo(id);
            setDebtorInputValue(match?.label ?? selected); // show full label in input
            if (creditTo !== id) {
              // debtor selection changed... note, this is not possible if editing but if user
              // creating new record, it is
              setInvoiceId(null);
              setSelectedOrderLineIds([]);
            }
          }}
          readOnly={
            debtorLoading || Number(form.values.creditNoteSummary.id) > 0
          }
          error={debtorError ?? undefined}
          style={{ zIndex: 3000 }}
          styles={{ label: { fontWeight: 600 } }}
        />

        <Autocomplete
          ref={invoiceRef}
          label="Invoice #"
          placeholder="Type invoice number"
          data={invoiceOptions}
          filter={invoiceOptionsFilter}
          value={invoiceInputValue}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
          onFocus={() => {
            invoiceRef.current?.select();
          }}
          onChange={setInvoiceInputValue} // updates as user types
          onOptionSubmit={(selected) => {
            const match = invoiceOptions.find(
              (d) => d.label === selected || d.value === selected,
            );
            const id = match ? Number(match.value) : 0;
            //form.setFieldValue("creditNoteSummary.invoiceId", id);
            setInvoiceId(id);
            setInvoiceInputValue(match?.label ?? selected); // show full label in input

            if (invoiceId !== id) {
              handleRemoveAllEntries();
              startTransition(() => {
                handlers.setState([]);
              });
            }
            setSelectedOrderLineIds([]);
          }}
          readOnly={
            invoiceOptions.length === 0 ||
            fetchingInvoices ||
            summary.isProcessed
          }
          error={debtorError ?? undefined}
          style={{ zIndex: 3000 }}
          styles={{ label: { fontWeight: 600 } }}
        />
        <NumberInput
          label="Gst"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={form.values.creditNoteSummary.gst}
          readOnly
        />
      </Group>
      <input
        type="hidden"
        {...form.getInputProps("creditNoteSummary.creditTo")}
      />
      <Textarea
        label="Notes"
        styles={{
          label: {
            fontWeight: 600, // makes label bold
          },
        }}
        {...form.getInputProps("creditNoteSummary.notes")}
        mt="md"
      />

      <Divider
        my="md"
        label={`Please select an invoice to load the order info. [${
          orderDetails.length ?? 0
        } items were loaded].`}
      />

      <Title order={3}>Product Entries</Title>
      <Group justify="space-between" mt="md">
        <DefaultButtonAdd
          disabled={summary.isProcessed}
          onClick={() => setModalOpen(true)}
          text="Select Product"
          variant="light"
        />
      </Group>

      <Stack gap="sm" className="form">
        <CreditNoteHeaderRow key={headerVersion} />
        {entries.map((entry, index) => (
          <CreditNoteEntryRow
            key={`${entry.productId}-${index}-${headerVersion}`}
            entry={entry}
            index={index}
            onUpdate={(updated) => debouncedUpdateRef.current(index, updated)}
            onRemove={() => handleRemoveEntry(index)}
          />
        ))}
      </Stack>

      <Group justify="space-between" mt="md">
        <DefaultButtonAdd
          onClick={() => setModalOpen(true)}
          disabled={summary.isProcessed}
          text="Select Product"
          variant="light"
        />

        <Group>
          <Button
            onClick={() => handleSubmit()}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || summary.isProcessed}
          >
            Save
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || summary.isProcessed}
          >
            Save & Close
          </Button>
          <Button
            onClick={handlePrint}
            disabled={saving || summary.id === 0}
            leftSection={<IconPrinter size={16} />}
          >
            Print
          </Button>
          <Button
            title="Process the journal. This action cannot be undone."
            onClick={handleProcess}
            disabled={saving || summary.isProcessed}
            leftSection={<IconCheck size={16} />}
          >
            Process
          </Button>
        </Group>
      </Group>

      <SavingModal opened={saving} onClose={() => setSaving(false)} />

      {/* product selections */}
      <Modal
        size={"xl"}
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          handleConfirmSelection();
        }}
        zIndex={3000}
        overlayProps={{ opacity: 0.3, blur: 2 }}
        title="Select Products to Credit"
      >
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <ScrollArea>
            {/* Master checkbox */}
            <Checkbox
              mb="sm"
              label="Select / Unselect All"
              checked={selectedOrderLineIds.length === orderDetails.length}
              indeterminate={
                selectedOrderLineIds.length > 0 &&
                selectedOrderLineIds.length < orderDetails.length
              }
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  setSelectedOrderLineIds(
                    orderDetails.map((p) => String(p.id)),
                  );
                } else {
                  setSelectedOrderLineIds([]);
                }
              }}
            />

            <Checkbox.Group
              value={selectedOrderLineIds}
              onChange={setSelectedOrderLineIds}
            >
              <Stack gap="sm">
                {orderDetails.map((product, index) => (
                  <Checkbox
                    key={`${index}-${product.id}`}
                    value={String(product.id)}
                    label={`${product.productId}${product.productCode}: ${product.productDescription} (${product.supplied} supplied)`}
                  />
                ))}
              </Stack>
            </Checkbox.Group>

            <DefaultButtonAdd
              style={{ marginTop: "8px" }}
              text="Add to Credit Note"
              onClick={handleConfirmSelection}
            />
          </ScrollArea>
        </div>
      </Modal>

      {/* Print preview modal */}
      <FullScreenModal opened={preview} onClose={() => setPreview(false)}>
        {recordId && <AdminCreditInvoicePreview id={recordId} />}
      </FullScreenModal>
    </Stack>
  );
};

export default AdminCreditNoteEntryPage;
